import { DraftMethods } from '../draft-api';
import type { FetchResponse, RestService, RequestParams, Interceptor } from '@sydle/ui-api';
import { buildGetParams, extractInfosByURL } from '../utils/url';
import type { SelectUserArgs } from './delegation-interceptor-utils';
import { hasDefinedDelegation, hasPendingDelegation, selectUser } from './delegation-interceptor-utils';

export class DraftDelegationInterceptor implements Interceptor {
  private delegationSelected = {};

  interceptorRequest = async (params: Partial<RequestParams>, isCompletedURL?: boolean): Promise<any> => {
    const requestInfos = extractInfosByURL(params.url, isCompletedURL);
    if (!this.isDraftsRequest(requestInfos.methodIdentifier)) return params;
    if (hasDefinedDelegation(params)) return params;
    const _checkDelegation = true;
    const _delegationUser = this.verifyDelegationUserSelected(requestInfos);
    if (params.method === 'GET') {
      params.url = buildGetParams(params.url, {
        _checkDelegation,
        _delegationUser,
      });
      return params;
    }

    return {
      ...params,
      method: 'POST',
      body: { ...params.body, _checkDelegation, _delegationUser },
    };
  };

  interceptorResponse = async (
    response: FetchResponse,
    requestParams: RequestParams,
    restService: RestService,
  ): Promise<any> => {
    const requestInfos = extractInfosByURL(response.meta.request.url, true);
    if (!this.isDraftsRequest(requestInfos.methodIdentifier)) return response;
    if (requestParams.body._delegationUser) {
      this.fillDelegationArray(requestInfos, requestParams.body._delegationUser, response);
      return response;
    }
    if (!hasPendingDelegation(response)) return response;
    const userOptions: SelectUserArgs[] = response.error.data._userDelegation;
    let delegationUser;
    if (this.isGetDraftRequest(requestInfos.methodIdentifier)) {
      delegationUser = userOptions[0];
    } else {
      delegationUser = await selectUser(userOptions);
    }
    if (!delegationUser) {
      return response;
    }
    this.fillDelegationArray(requestInfos, delegationUser.reference, response, true);
    requestParams.body._delegationUser = delegationUser.reference;

    requestParams.addBaseUrl = false;
    const result = await restService.fetch(requestParams);
    return result;
  };

  private isDraftsRequest(methodIdentifier: string): boolean {
    return (
      this.isGetDraftRequest(methodIdentifier) ||
      this.isCreateDraftRequest(methodIdentifier) ||
      this.isGetDraftsRequest(methodIdentifier) ||
      this.isPublishDraftRequest(methodIdentifier)
    );
  }

  private isGetDraftsRequest(methodIdentifier: string): boolean {
    return methodIdentifier && methodIdentifier.indexOf(DraftMethods.MULTIPLE_GET) !== -1;
  }

  private isGetDraftRequest(methodIdentifier: string): boolean {
    return methodIdentifier === DraftMethods.GET;
  }

  private isCreateDraftRequest(methodIdentifier: string): boolean {
    return methodIdentifier && methodIdentifier.indexOf(`${DraftMethods.CREATE}`) !== -1;
  }

  private isPublishDraftRequest(methodIdentifier: string): boolean {
    return methodIdentifier === DraftMethods.PUBLISH;
  }

  private fillDelegationArray(
    requestInfos: any,
    delegationUser: any,
    response: FetchResponse,
    ignoreDraftRequest?: boolean,
  ): void {
    const methodIdentifier: string = requestInfos.methodIdentifier;
    if (this.isGetDraftsRequest(methodIdentifier)) {
      this.delegationSelected[requestInfos.classId] = delegationUser;
      return;
    }
    if (ignoreDraftRequest) return;

    if (this.isCreateDraftRequest(methodIdentifier)) {
      this.delegationSelected[`${requestInfos.classId}:${response.data._id}`] = delegationUser;
      return;
    }

    if (this.isGetDraftRequest(methodIdentifier) && response.data._creationUser) {
      this.delegationSelected[`${response.data._class?._id}:${response.data._id}`] = response.data._creationUser;
      return;
    }
    return;
  }

  private verifyDelegationUserSelected(requestInfos: any): string {
    const methodIdentifier: string = requestInfos.methodIdentifier;
    if (this.isCreateDraftRequest(methodIdentifier)) {
      return this.delegationSelected[requestInfos.classId];
    }
    if (!this.isGetDraftsRequest(methodIdentifier)) {
      return this.delegationSelected[`${requestInfos.classId}:${requestInfos.objectId}`];
    }
    if (!this.isPublishDraftRequest(methodIdentifier)) {
      return this.delegationSelected[`${requestInfos.classId}:${requestInfos.objectId}`];
    }

    return null;
  }
}
